import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { FeedsFrame, Pill, num } from '../components/FeedsNav'
import { useMerchSets, useMerchThemes, useUpdateSet, useUpdateTheme } from '../hooks/useFeeds'
import { LIFECYCLES, MARGIN_BANDS, REASON_LABEL, type MarginBand, type MerchSet, type MerchTheme, type ThemeLifecycle } from '../api/feedsApi'

const ALL = '__all__'
const NONE = '__none__'

function SeedConcepts({ theme }: { theme: MerchTheme }) {
  const update = useUpdateTheme()
  const initial = theme.seedConcepts.join(', ')
  const [value, setValue] = useState(initial)
  const dirty = value !== initial
  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(e) => {
        e.preventDefault()
        update.mutate({ slug: theme.slug, patch: { seedConcepts: value.split(',').map((s) => s.trim()).filter(Boolean) } })
      }}
    >
      <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="comma, separated" className="h-8 min-w-[12rem] text-xs" />
      {dirty && <Button type="submit" size="sm" variant="outline" className="h-8" disabled={update.isPending}>Save</Button>}
    </form>
  )
}

function ThemeRow({ theme, linkBase, onShowSets }: { theme: MerchTheme; linkBase: string | null; onShowSets: () => void }) {
  const update = useUpdateTheme()
  const sets = theme.sets ?? { total: 0, live: 0, draft: 0, retired: 0 }
  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{theme.name}</div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="font-mono">{theme.slug}</span>
          {theme.kind !== 'room' && <Pill tone="slate">{theme.kind}</Pill>}
        </div>
        {linkBase ? (
          <a href={linkBase + theme.canonicalPath} target="_blank" rel="noreferrer" className="text-xs text-sky-700 hover:underline">
            {theme.canonicalPath}
          </a>
        ) : <span className="text-xs text-slate-500">{theme.canonicalPath}</span>}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {num(theme.members)}
        <div>
          <Link to={`/app/feeds/items?theme=${theme.slug}`} className="text-xs text-sky-700 hover:underline" title="Items whose custom_label_1 is this room">
            items
          </Link>
        </div>
      </TableCell>
      <TableCell>
        <button type="button" onClick={onShowSets} className="text-left text-xs hover:underline">
          <span className="tabular-nums">{sets.total}</span> total
          <div className="text-slate-500">{sets.live} live · {sets.draft} draft · {sets.retired} retired</div>
        </button>
      </TableCell>
      <TableCell>
        <Select
          value={theme.lifecycle}
          onValueChange={(v) => update.mutate({ slug: theme.slug, patch: { lifecycle: v as ThemeLifecycle } })}
          disabled={update.isPending}
        >
          <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LIFECYCLES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Select
          value={theme.marginBand ?? NONE}
          onValueChange={(v) => update.mutate({ slug: theme.slug, patch: { marginBand: v === NONE ? null : (v as MarginBand) } })}
          disabled={update.isPending}
        >
          <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>—</SelectItem>
            {MARGIN_BANDS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell><SeedConcepts key={theme.seedConcepts.join(',')} theme={theme} /></TableCell>
    </TableRow>
  )
}

function SetFeedStatus({ s }: { s: MerchSet }) {
  if (!s.inFeed) return <Pill tone="slate" title="No feed item: retired sets are not built, or no build has run since it was added.">not in feed</Pill>
  if (s.eligible) return <Pill tone="green">eligible</Pill>
  return <Pill tone="amber" title={s.exclusionReason ? REASON_LABEL[s.exclusionReason] : undefined}>{s.exclusionReason ?? 'excluded'}</Pill>
}

export function RoomsSetsPage() {
  const { data: themes, isLoading: themesLoading } = useMerchThemes()
  const { data: sets, isLoading: setsLoading } = useMerchSets()
  const updateSet = useUpdateSet()
  const [themeFilter, setThemeFilter] = useState<string>(ALL)
  const [statusFilter, setStatusFilter] = useState<string>(ALL)
  const [confirm, setConfirm] = useState<{ set: MerchSet; status: 'draft' | 'retired' } | null>(null)

  const linkBase = sets?.linkBase ?? null
  const visible = (sets?.data ?? []).filter((s) =>
    (themeFilter === ALL || s.themeSlug === themeFilter) && (statusFilter === ALL || s.status === statusFilter))

  return (
    <FeedsFrame
      title="Rooms & Sets"
      description={
        <>
          The merchandising registry. A room&apos;s slug is the product feed&apos;s custom_label_1 and the page feed&apos;s room label;
          lifecycle and margin band label the pages and items. Sets go live only by minting (scripts/feeds-admin.js mint-sets);
          here they can be held as draft or retired. Changes reach the feed on the next build.
        </>
      }
    >
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Rooms</CardTitle>
          <CardDescription>Members are distinct artworks in the room (collection, set or graph membership).</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Room</TableHead>
                <TableHead className="text-right">Members</TableHead>
                <TableHead>Sets</TableHead>
                <TableHead>Lifecycle</TableHead>
                <TableHead>Margin</TableHead>
                <TableHead>Seed concepts</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {themesLoading && <TableRow><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>}
              {!themesLoading && themes?.data.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">No rooms yet. Seed them with scripts/feeds-admin.js import-sets.</TableCell></TableRow>
              )}
              {themes?.data.map((t) => (
                <ThemeRow key={t.slug} theme={t} linkBase={linkBase} onShowSets={() => setThemeFilter(t.slug)} />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 pb-2">
          <div>
            <CardTitle className="text-sm">Gallery-wall sets ({visible.length})</CardTitle>
            <CardDescription>Feed status is the google-us item for the set (SET-&lt;slug&gt;) as of the last build.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Select value={themeFilter} onValueChange={setThemeFilter}>
              <SelectTrigger className="h-8 w-48 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All rooms</SelectItem>
                {themes?.data.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any status</SelectItem>
                <SelectItem value="live">live</SelectItem>
                <SelectItem value="draft">draft</SelectItem>
                <SelectItem value="retired">retired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16" />
                <TableHead>Set</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Pieces</TableHead>
                <TableHead>Feed</TableHead>
                <TableHead>Price</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {setsLoading && <TableRow><TableCell colSpan={7}><Skeleton className="h-8 w-full" /></TableCell></TableRow>}
              {!setsLoading && visible.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">No sets match.</TableCell></TableRow>
              )}
              {visible.map((s) => (
                <TableRow key={s.slug}>
                  <TableCell>
                    {s.imageUrl
                      ? <img src={s.imageUrl} alt="" loading="lazy" className="h-12 w-12 rounded object-cover" />
                      : <div className="h-12 w-12 rounded bg-slate-100" />}
                  </TableCell>
                  <TableCell className="max-w-sm">
                    <div className="truncate font-medium">{s.name}</div>
                    <div className="truncate text-xs text-slate-500">
                      <span className="font-mono">{s.slug}</span>{s.themeName && <> · {s.themeName}</>}
                    </div>
                    {s.handle && linkBase && (
                      <a href={`${linkBase}/products/${s.handle}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline">
                        /products/{s.handle} <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </TableCell>
                  <TableCell>
                    <Pill tone={s.status === 'live' ? 'green' : s.status === 'draft' ? 'amber' : 'slate'}>{s.status}</Pill>
                    {s.statusReason && <div className="mt-1 max-w-xs text-xs text-slate-500" title={s.statusReason}>{s.statusReason}</div>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={s.pieceHandles.join('\n')}>{s.pieces}</TableCell>
                  <TableCell><SetFeedStatus s={s} /></TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">{s.price ?? '—'}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {s.status !== 'draft' && (
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => setConfirm({ set: s, status: 'draft' })}>Draft</Button>
                    )}
                    {s.status !== 'retired' && (
                      <Button size="sm" variant="ghost" className="h-8 text-red-700" onClick={() => setConfirm({ set: s, status: 'retired' })}>Retire</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.status === 'retired' ? 'Retire' : 'Move to draft'}: {confirm?.set.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.status === 'retired'
                ? 'The set leaves the product and page feeds on the next build, and minting skips it. Its Shopify product is not changed.'
                : 'The set is excluded from the feed (not_on_sale) on the next build. Minting with --publish can make it live again. Its Shopify product is not changed.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirm) updateSet.mutate({ slug: confirm.set.slug, status: confirm.status })
                setConfirm(null)
              }}
            >
              {confirm?.status === 'retired' ? 'Retire' : 'Move to draft'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FeedsFrame>
  )
}
